import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, Search, UserPlus } from "lucide-react";
import { api, apiErrorMessage } from "../../../lib/api";
import { JourneyCell, JourneySummary } from "../GroupJourney";
import { matchesWaits } from "../../../lib/groupJourney";
import { TEAM, VIEWS } from "../../../lib/groupSheet";
import AddParticipantDialog from "../AddParticipantDialog";

// No seat, so listed apart from the cohort (29.10.2026). Expired ones have their own view.
const LEFT = ["cancelled", "failed", "refunded", "abandoned"];
const STOPPED = ["cancelled", "failed"];

const STATUS_BADGE = {
  submitted: "bg-[#3F8EBA]/15 text-[#3F8EBA]",
  pending_payment: "bg-[#FDE68A] text-[#8a6a1a]",
  confirmed: "bg-[#965EA7]/15 text-[#7A4986]",
  paid: "bg-[#519872]/15 text-[#519872]",
  cancelled: "bg-[#FDBA74]/30 text-[#9A3412]",
  failed: "bg-[#E63946]/15 text-[#E63946]",
  abandoned: "bg-[#6B7280]/15 text-[#6B7280]",
  refunded: "bg-[#6B7280]/15 text-[#6B7280]",
};

/**
 * The sheet's first tab: the six counts (decision C), the search right above
 * the table, the steps summary as the filter, and the switch between those in
 * the group, those who left and those expired (decision B).
 */
export default function ParticipantsTab({ groupId, data, journey, currentRole, onReload }) {
  const [search, setSearch] = useState("");
  const [waitsFilter, setWaitsFilter] = useState("");
  const [view, setView] = useState("in");
  const [sending, setSending] = useState(null);
  const [adding, setAdding] = useState(false);
  const team = TEAM.includes(currentRole);
  const stats = data.stats || {};

  const found = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data.participants || []).filter((p) => !q || [p.nume, p.prenume, p.email, p.telefon, p.unitate, p.specializare]
      .filter(Boolean).join(" ").toLowerCase().includes(q));
  }, [data, search]);

  const groups = useMemo(() => ({
    in: found.filter((p) => !p.payment_expiry_reason && !LEFT.includes(p.status)),
    // Cancelled, refused, refunded (29.10.2026): no seat, so not among the cohort either.
    left: found.filter((p) => !p.payment_expiry_reason && LEFT.includes(p.status)),
    // Closed by the payment deadline (23.09.2026).
    expired: found.filter((p) => p.payment_expiry_reason),
  }), [found]);
  const active = groups.in.filter((p) => matchesWaits(journey, p.id, waitsFilter));

  const toLeads = async (p) => {
    setSending(p.id);
    try {
      await api.post(`/admin/enrollments/${p.id}/payment-stopped/lead`);
      toast.success(`${p.prenume} ${p.nume} e la Leaduri, în „De sunat azi”.`);
      await onReload();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Nu am putut trimite la Leaduri."));
    } finally {
      setSending(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="group-tab-participanti">
      <section className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2" data-testid="group-stats">
        <Stat label="Total activi" value={stats.total} accent="bg-[#373A45] text-white" />
        <Stat label="Plătite" value={stats.paid} accent="bg-[#519872]/15 text-[#519872]" />
        <Stat label="Confirmate" value={stats.confirmed} accent="bg-[#3F8EBA]/15 text-[#1f5d80]" />
        <Stat label="Trimise" value={stats.submitted} accent="bg-[#965EA7]/15 text-[#7A4986]" />
        <Stat label="Așteaptă plată" value={stats.pending_payment} accent="bg-[#FDE68A] text-[#8a6a1a]" />
        <Stat label="Locuri libere" value={stats.spots_left ?? "—"} accent="bg-[#EDEEF0] text-[#373A45]" />
      </section>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
          <input type="search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Caută după nume, email, telefon, unitate…"
            className="w-full bg-[#F5F6F6] border border-[#E5E7EB] rounded-full pl-8 pr-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-[#965EA7]"
            data-testid="group-details-search" />
        </div>
        {/* Straight into the group, whatever the site's window (03.11.2026): owner and admin FPED. */}
        {team && (
          <button type="button" onClick={() => setAdding(true)} data-testid="group-add-participant"
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-[#965EA7]/40 px-3 py-1.5 text-xs font-semibold text-[#7A4986] hover:bg-[#965EA7]/10">
            <UserPlus size={14} /> Adaugă cursant
          </button>
        )}
      </div>
      <AddParticipantDialog groupId={groupId} open={adding} onClose={() => setAdding(false)} onAdded={onReload} />

      <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Cine apare în listă" data-testid="group-participants-views">
        {VIEWS.map((v) => {
          const n = groups[v.key].length;
          if (v.key !== "in" && n === 0) return null;
          const on = view === v.key;
          return (
            <button key={v.key} type="button" role="tab" aria-selected={on} onClick={() => setView(v.key)}
              data-testid={`group-view-${v.key}`}
              className={`rounded-full px-3 py-1 text-xs font-semibold ${on ? "bg-[#373A45] text-white" : "border border-[#E5E7EB] text-[#373A45] hover:bg-[#F5F6F6]"}`}>
              {v.label} {n}
            </button>
          );
        })}
      </div>

      {view === "in" && (
        <section>
          <JourneySummary journey={journey} filter={waitsFilter} onFilter={setWaitsFilter} />
          {active.length === 0 ? (
            <div className="text-center py-12 text-[#6B7280] text-sm">Niciun participant pentru filtrele curente.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F5F6F6] text-[#373A45]">
                  <tr className="text-left"><Th>Nume</Th><Th>Contact</Th><Th>Etapa</Th><Th>Înregistrat</Th></tr>
                </thead>
                <tbody>
                  {active.map((p) => (
                    <tr key={p.id} className="border-t border-[#E5E7EB] hover:bg-[#F5F6F6]" data-testid={`participant-row-${p.id}`}>
                      <td className="px-3 py-2.5 align-top">
                        <div className="font-semibold text-[#373A45]">{p.prenume} {p.nume}</div>
                        <div className="text-[11px] text-[#6B7280]">{p.specializare || ""}{p.unitate ? ` · ${p.unitate}` : ""}</div>
                        {p.notes_count > 0 && <div className="text-[10px] text-[#6B7280] mt-1">{p.notes_count} notițe</div>}
                        <a href={`/admin/enrollments?e=${p.id}`} target="_blank" rel="noopener noreferrer"
                          className="mt-1.5 inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-[#E5E7EB] px-2 py-0.5 text-[10px] font-semibold text-[#7A4986] hover:bg-[#965EA7]/10"
                          data-testid={`open-enrollment-${p.id}`}>
                          <ExternalLink size={10} /> Fișa înscrierii
                        </a>
                      </td>
                      <td className="px-3 py-2.5 align-top text-xs">
                        <div>{p.email}</div>
                        <div className="text-[#6B7280]">{p.telefon}</div>
                      </td>
                      <td className="px-3 py-2.5 align-top">
                        {journey ? <JourneyCell row={journey.rows?.[p.id]} /> : (
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_BADGE[p.status] || ""}`}>{p.status || "—"}</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 align-top text-xs text-[#6B7280]">
                        {p.created_at ? new Date(p.created_at).toLocaleString("ro-RO") : "—"}
                        {p.utm_campaign && <div className="text-[10px]">UTM: {p.utm_campaign}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {view === "left" && (
        <section data-testid="group-details-left">
          <p className="mb-2 text-[11px] text-[#6B7280]">
            Plata anulată, respinsă sau rambursată: nu ocupă locuri și nu primesc emailurile grupei. Cei cu plata anulată sau
            respinsă ajung singuri la Leaduri, în „De sunat azi”; pe cei de dinainte de 29.10 îi trimiți cu „Trimite la Leaduri”.
          </p>
          <ul className="space-y-1 text-xs">
            {groups.left.map((p) => (
              <li key={p.id} className="flex flex-wrap items-baseline justify-between gap-2 rounded-lg bg-[#F5F6F6] px-2.5 py-1.5"
                data-testid={`left-row-${p.id}`}>
                <span>
                  <span className="font-semibold text-[#373A45]">{p.prenume} {p.nume}</span>
                  <span className="ml-1.5 text-[#6B7280]">{p.email}{p.telefon ? ` · ${p.telefon}` : ""}</span>
                </span>
                <span className="flex flex-wrap items-baseline gap-2">
                  <span className="text-[#9b1f2c]">
                    {p.left_reason || p.status}{p.updated_at ? ` · ${new Date(p.updated_at).toLocaleDateString("ro-RO")}` : ""}
                  </span>
                  {STOPPED.includes(p.status) && (p.payment_stopped_lead_at ? (
                    <span className="text-[#3c7457]">la Leaduri</span>
                  ) : team && (
                    <button type="button" onClick={() => toLeads(p)} disabled={sending === p.id}
                      className="font-semibold text-[#965EA7] hover:underline disabled:opacity-50"
                      data-testid={`left-to-leads-${p.id}`}>
                      {sending === p.id ? "Se trimite…" : "Trimite la Leaduri"}
                    </button>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {view === "expired" && (
        <section data-testid="group-details-expired">
          <p className="mb-2 text-[11px] text-[#6B7280]">
            Nu au plătit la termen (48 de ore de la începerea plății, 24 când grupa mai avea cel mult 3 locuri), deci nu mai
            ocupă locuri. Îi găsești la Leaduri, în „De sunat azi”.
          </p>
          <ul className="space-y-1 text-xs">
            {groups.expired.map((p) => (
              <li key={p.id} className="flex flex-wrap items-baseline justify-between gap-2 rounded-lg bg-[#F5F6F6] px-2.5 py-1.5">
                <span>
                  <span className="font-semibold text-[#373A45]">{p.prenume} {p.nume}</span>
                  <span className="ml-1.5 text-[#6B7280]">{p.email}{p.telefon ? ` · ${p.telefon}` : ""}</span>
                </span>
                <span className="text-[#8a6a1a]">
                  expirată{p.payment_expired_at ? ` pe ${new Date(p.payment_expired_at).toLocaleDateString("ro-RO")}` : ""}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

const Th = ({ children }) => (
  <th className="px-3 py-2 text-xs uppercase tracking-widest font-semibold whitespace-nowrap">{children}</th>
);

function Stat({ label, value, accent = "bg-[#EDEEF0] text-[#373A45]" }) {
  return (
    <div className={`rounded-xl px-3 py-2 text-center ${accent}`}>
      <div className="text-xl font-bold">{value}</div>
      <div className="text-[10px] uppercase tracking-widest font-semibold opacity-75">{label}</div>
    </div>
  );
}
